// Cross-step rules that only make sense against the whole list: one score,
// and a split step's threshold kept pointing at a value the score's scale
// still produces. Shared by the (old) steps editor and, from Task 11, the
// per-step sections — both edit the same whole-list form.

import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { metricIndex, metricValues, type StepFormValue, type StepsFormValues } from "../survey-schema";

/** "Floor of the median" of the scored step's values — a split threshold's
 *  fallback when a scale change leaves it pointing at a value no option
 *  produces anymore. Picked over the lowest value because a threshold at
 *  the very bottom would route almost every response to the high-score
 *  options. */
function lowerMedianValue(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor((sorted.length - 1) / 2);
    return sorted[mid] ?? 0;
}

function isOffScale(step: StepFormValue, scoreValues: number[]): boolean {
    return (
        scoreValues.length > 0 &&
        step.kind === "multi" &&
        step.multiMode === "split" &&
        !scoreValues.includes(step.threshold)
    );
}

/** The list with every split threshold the reconcile below would move
 *  already moved. A per-step section seeds its form from this, so the
 *  reconcile finds nothing to do on mount and a stored off-scale threshold
 *  never reads as the user's own edit. */
export function reconcileSplitThresholds(steps: StepFormValue[]): StepFormValue[] {
    const scoreValues = metricValues({ steps });
    if (!steps.some((step) => isOffScale(step, scoreValues))) return steps;
    return steps.map((step) =>
        isOffScale(step, scoreValues) ? { ...step, threshold: lowerMedianValue(scoreValues) } : step,
    );
}

export interface StepsRules {
    values: StepsFormValues;
    scoredAt: number;
    scoreValues: number[];
    toggleMetric: (index: number, next: boolean) => void;
}

export function useStepsRules(form: UseFormReturn<StepsFormValues>): StepsRules {
    const values = form.watch();
    const scoreValues = metricValues(values);

    // A scale change on the scored step can leave a split threshold pointing
    // at a value no option produces anymore — reconcile every split step.
    useEffect(() => {
        values.steps.forEach((step, index) => {
            if (isOffScale(step, scoreValues)) {
                form.setValue(`steps.${index}.threshold`, lowerMedianValue(scoreValues), { shouldDirty: true });
            }
        });
    }, [form, scoreValues, values.steps]);

    function toggleMetric(index: number, next: boolean) {
        values.steps.forEach((_, other) => {
            if (other !== index) form.setValue(`steps.${other}.isMetric`, false, { shouldDirty: true });
        });
        form.setValue(`steps.${index}.isMetric`, next, { shouldDirty: true });
    }

    return { values, scoredAt: metricIndex(values), scoreValues, toggleMetric };
}
